import React, { useCallback, useMemo } from "react";
import LeadGenV0 from "./LeadGenV0";
import useExperience from "../../hooks/useExperience";
import { WIDGET_TYPE, WIDGET_VERSION } from "../../conf";
import LeadGenV1 from "./LeadGenV1";
import useData from "../../hooks/useData";
import "./lead-gen.css";
import {
	Button,
	PaddedContainer,
	SPACING,
	StatusLabel,
	Text,
} from "@disco/disco_core";
import useWidget from "../../hooks/useWidget";
import inPreview from "../../utils/inPreview";
import useDimensions from "../../hooks/useDimensions";
import message from "../../utils/message";
import { MESSAGE_TYPES } from "../../hooks/useMessage";
import useWidgetOffer from "../../hooks/useWidgetOffer";
import useMode from "../../hooks/useMode";
import useLeadGenV0Config from "../../hooks/useLeadGenV0Config";
import { WidgetOfferManager } from "../managers";

export const generateProductRowData = ({ data }) => {
	const extractedProducts = data?.recommendations?.map(
		({ products, ...brand }) => ({ ...products[0], brand })
	);
	if (!Array.isArray(extractedProducts)) return [];
	return extractedProducts;
};

const LeadGenPlatformWarning = ({ ...rest }) => {
	const data = useData();
	const shouldShowWarning = inPreview && !data?.previewMeta?.hasNurtureOffer;

	const handleClick = () => {
		message(MESSAGE_TYPES.PLATFORM_INTERNAL_REDIRECT, {
			name: "discoFeed",
			queryParams: "?tab=1",
		});
	};

	if (!shouldShowWarning) return null;

	return (
		<PaddedContainer {...rest}>
			<StatusLabel
				type="info"
				noBorder
				noShadow
				className="lead-gen-platform-warning"
				vPadding={"sm"}
				marginBottom={"-10px"}
				marginTop={SPACING.REGULAR}
			>
				<Text size={"lg-body"} thick>
					Boost your visibility with offers
				</Text>
				<Text size={"body"} marginTop={"mc"}>
					Create an offer to unlock prime placement and drive new
					conversions
				</Text>
				<Button secondary marginTop={"sm"} onClick={handleClick}>
					Create offer
				</Button>
			</StatusLabel>
		</PaddedContainer>
	);
};

const LeadGenBackgroundWrapper = ({ children }) => {
	const { isInline } = useMode();
	const { inDesktopOnly } = useDimensions();

	// No need to apply any additional styles
	if (isInline) return children;

	return (
		<PaddedContainer
			className="lead-gen-background-wrapper"
			vPadding={inDesktopOnly ? SPACING.MEGA : SPACING.JUMBO}
			tightBottom
		>
			<PaddedContainer
				style={{
					flexGrow: 0,
					width: isInline
						? "100%"
						: `min(${inDesktopOnly ? 470 : 355}px, 95%)`,
				}}
			>
				{children}
			</PaddedContainer>
		</PaddedContainer>
	);
};

const LeadGen = () => {
	const { activeWidgetVersion } = useExperience();
	const { containerRef } = useData();
	const { isInline } = useMode();
	const {
		NUM_OFFERS_IDX: LEAD_GEN_NUM_BRAND_IDX,
		TOTAL_OFFERS: totalOffers,
		areOffersEmpty,

		activeOffer: activeBrand,
		activeOfferIdx: activeBrandIdx,

		claimOffer,
		claimedOffers,
		isOfferClaimed,

		showNextOffer: showNextBrand,
		showPrevOffer: showPrevBrand,
		isLastOffer,
		setActiveOfferIdx,
	} = useWidgetOffer();

	const { delayedTriggerResize } = useWidget({
		isEmpty: areOffersEmpty,
		containerRef,
		canTrackLoad: true,
		brandsRemoteKey: "brands",
		intersectionId: "lead-gen",
	});

	const triggerResize = useCallback(() => {
		if (!isInline) return;
		delayedTriggerResize(0)();
	}, [isInline, delayedTriggerResize]);

	const { isLargeWidget } = useLeadGenV0Config();

	const COMPUTED_PADDING = isLargeWidget ? 20 : 16; // Hard Coding values for compatibility with carousel

	const baseComponentProps = {
		LEAD_GEN_NUM_BRAND_IDX,
		totalOffers,
		activeBrand,

		claimOffer,
		claimedOffers,
		isOfferClaimed,

		isLastOffer,
		setActiveOfferIdx,
		activeBrandIdx,
		showNextBrand,
		showPrevBrand,
		triggerWidgetResize: triggerResize,
		COMPUTED_PADDING,
	};

	const Component = useMemo(() => {
		let Component = null;

		switch (activeWidgetVersion) {
			case WIDGET_VERSION[WIDGET_TYPE.LEAD_GEN][1.0]:
				Component = LeadGenV1;
				break;
			default:
				Component = LeadGenV0;
				break;
		}

		return Component;
	}, [activeWidgetVersion]);

	return (
		<>
			<LeadGenPlatformWarning hPadding={COMPUTED_PADDING} />
			<LeadGenBackgroundWrapper>
				<Component {...baseComponentProps} />
			</LeadGenBackgroundWrapper>
		</>
	);
};

export default () => {
	return (
		<WidgetOfferManager>
			<LeadGen />
		</WidgetOfferManager>
	);
};
